import { useMemo, useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import { divIcon } from 'leaflet'
import PropTypes from 'prop-types'
import {
  countKeyServices,
  getAvailableServices,
  getAvailableInfrastructure,
} from '../../../utils/facilityUtils'
import 'leaflet/dist/leaflet.css'

/**
 * Health Facility Map Component
 *
 * Interactive map showing health facilities for the current location
 */
function HealthFacilityMap({ facilities, location, title }) {
  // State for selected facility modal
  const [selectedFacilityId, setSelectedFacilityId] = useState(null)

  // Find the selected facility
  const selectedFacility = useMemo(() => {
    if (!selectedFacilityId || !facilities) return null
    return facilities.find(f => f.id === selectedFacilityId)
  }, [selectedFacilityId, facilities])

  // Close modal on escape key and manage body overflow
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape') {
        setSelectedFacilityId(null)
      }
    }

    if (selectedFacilityId) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleEscape)

      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.body.style.overflow = originalOverflow
      }
    } else {
      document.body.style.overflow = ''
    }
  }, [selectedFacilityId])

  // Safety cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  // Calculate center and bounds from facilities
  const { center, bounds } = useMemo(() => {
    if (!facilities || facilities.length === 0) {
      // Default to Uganda center
      return {
        center: [1.3733, 32.2903],
        bounds: null,
      }
    }

    const validFacilities = facilities.filter(
      f =>
        f.latitude && f.longitude && !isNaN(f.latitude) && !isNaN(f.longitude)
    )

    if (validFacilities.length === 0) {
      return {
        center: [1.3733, 32.2903],
        bounds: null,
      }
    }

    const lats = validFacilities.map(f => f.latitude)
    const lons = validFacilities.map(f => f.longitude)

    const centerLat = (Math.max(...lats) + Math.min(...lats)) / 2
    const centerLon = (Math.max(...lons) + Math.min(...lons)) / 2

    return {
      center: [centerLat, centerLon],
      bounds: [
        [Math.min(...lats), Math.min(...lons)],
        [Math.max(...lats), Math.max(...lons)],
      ],
    }
  }, [facilities])

  // Create custom marker icon based on facility level
  const createMarkerIcon = level => {
    const levelConfig = {
      HCII: { color: '#10b981', size: 20 },
      HCIII: { color: '#3b82f6', size: 24 },
      HCIV: { color: '#f59e0b', size: 28 },
      Hospital: { color: '#ef4444', size: 32 },
      Clinic: { color: '#8b5cf6', size: 18 },
    }

    const config = levelConfig[level] || { color: '#6b7280', size: 20 }

    return divIcon({
      html: `
        <div style="
          background-color: ${config.color};
          width: ${config.size}px;
          height: ${config.size}px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-center: center;
        ">
          <span style="
            transform: rotate(45deg);
            font-size: 12px;
            color: white;
          ">🏥</span>
        </div>
      `,
      className: 'custom-marker',
      iconSize: [config.size, config.size],
      iconAnchor: [config.size / 2, config.size],
    })
  }

  // Create cluster icon
  const createClusterIcon = cluster => {
    const count = cluster.getChildCount()
    return divIcon({
      html: `<div class="cluster-marker"><span class="cluster-count">${count}</span></div>`,
      className: 'custom-cluster-icon',
      iconSize: [35, 35],
    })
  }

  const validFacilities = facilities.filter(
    f => f.latitude && f.longitude && !isNaN(f.latitude) && !isNaN(f.longitude)
  )

  if (validFacilities.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm">
          No facilities with location coordinates available for this area.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 mb-4">
        Interactive map showing {validFacilities.length} health{' '}
        {validFacilities.length === 1 ? 'facility' : 'facilities'} in{' '}
        {location?.name || 'this location'}
      </p>

      {/* Map Container */}
      <div className="h-96 rounded-lg overflow-hidden border border-gray-200">
        <MapContainer
          center={center}
          zoom={bounds ? undefined : 10}
          bounds={bounds}
          style={{ height: '100%', width: '100%' }}
          className="leaflet-container"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MarkerClusterGroup
            iconCreateFunction={createClusterIcon}
            chunkedLoading
            maxClusterRadius={60}
          >
            {validFacilities.map((facility, index) => (
              <Marker
                key={index}
                position={[facility.latitude, facility.longitude]}
                icon={createMarkerIcon(facility.level)}
              >
                <Popup className="custom-popup" maxWidth={300}>
                  <div className="min-w-48">
                    <h3 className="font-medium text-gray-900 mb-1 capitalize">
                      {facility.name}
                    </h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700">
                          Level:
                        </span>
                        <span className="ml-1 text-gray-600">
                          {facility.level}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700">
                          Location:
                        </span>
                        <span className="ml-1 text-gray-600">
                          {facility.parish && `${facility.parish}, `}
                          {facility.district}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700">
                          Key Services:
                        </span>
                        <span className="ml-1 text-gray-600">
                          {countKeyServices(facility)} available
                        </span>
                      </div>
                      {facility.officer_in_charge && (
                        <div className="flex items-center">
                          <span className="font-medium text-gray-700">
                            Officer:
                          </span>
                          <span className="ml-1 text-gray-600 capitalize">
                            {facility.officer_in_charge.toLowerCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedFacilityId(facility.id)}
                      className="mt-2 w-full bg-primary-500 text-white text-sm py-1 px-2 rounded hover:bg-primary-600 transition-colors"
                    >
                      View Details
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
      </div>

      {/* Map Legend */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
          <span className="text-gray-700">HC II</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-gray-700">HC III</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
          <span className="text-gray-700">HC IV</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className="text-gray-700">Hospital</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-violet-500"></div>
          <span className="text-gray-700">Clinic</span>
        </div>
      </div>

      {/* Add cluster marker styles */}
      <style>{`
        .custom-marker {
          background: none;
          border: none;
        }

        .custom-cluster-icon {
          background: none;
          border: none;
        }

        .cluster-marker {
          background-color: #3b82f6;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 35px;
          height: 35px;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .cluster-count {
          color: white;
          font-weight: bold;
          font-size: 14px;
          font-family: Poppins, system-ui, -apple-system, sans-serif;
        }

        .leaflet-popup-content-wrapper {
          font-family: Poppins, system-ui, -apple-system, sans-serif;
        }
      `}</style>

      {/* Facility Detail Modal */}
      {selectedFacilityId && selectedFacility && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto shadow-xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 capitalize">
                  {selectedFacility.name}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedFacility.level} •{' '}
                  {selectedFacility.parish && `${selectedFacility.parish}, `}
                  {selectedFacility.district}
                </p>
              </div>
              <button
                onClick={() => setSelectedFacilityId(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Contact Information */}
              {(selectedFacility.officer_in_charge ||
                selectedFacility.phone ||
                selectedFacility.email) && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-xl">👤</span>
                    Contact Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    {selectedFacility.officer_in_charge && (
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-gray-700 min-w-[120px]">
                          Officer in Charge:
                        </span>
                        <span className="text-gray-900 capitalize">
                          {selectedFacility.officer_in_charge.toLowerCase()}
                        </span>
                      </div>
                    )}
                    {selectedFacility.phone && (
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-gray-700 min-w-[120px]">
                          Phone:
                        </span>
                        <span className="text-gray-900">
                          {selectedFacility.phone}
                        </span>
                      </div>
                    )}
                    {selectedFacility.email && (
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-gray-700 min-w-[120px]">
                          Email:
                        </span>
                        <span className="text-gray-900">
                          {selectedFacility.email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Services */}
              {(() => {
                const services = getAvailableServices(selectedFacility)
                return services.length > 0 ? (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="text-xl">🏥</span>
                      Available Services ({services.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {services.map((service, index) => (
                        <div
                          key={index}
                          className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2"
                        >
                          <span className="text-lg">{service.icon}</span>
                          <span className="text-sm text-gray-800">
                            {service.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-600">
                    <span className="text-2xl mb-2 block">ℹ️</span>
                    No service information available
                  </div>
                )
              })()}

              {/* Infrastructure */}
              {(() => {
                const infrastructure =
                  getAvailableInfrastructure(selectedFacility)
                return infrastructure.length > 0 ? (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="text-xl">🏗️</span>
                      Infrastructure ({infrastructure.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {infrastructure.map((item, index) => (
                        <div
                          key={index}
                          className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2"
                        >
                          <span className="text-lg">{item.icon}</span>
                          <span className="text-sm text-gray-800">
                            {item.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null
              })()}

              {/* Location Details */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="text-xl">📍</span>
                  Location Details
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  {selectedFacility.district && (
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        District:
                      </span>
                      <span className="text-gray-900">
                        {selectedFacility.district}
                      </span>
                    </div>
                  )}
                  {selectedFacility.subcounty && (
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Subcounty:
                      </span>
                      <span className="text-gray-900">
                        {selectedFacility.subcounty}
                      </span>
                    </div>
                  )}
                  {selectedFacility.parish && (
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Parish:
                      </span>
                      <span className="text-gray-900">
                        {selectedFacility.parish}
                      </span>
                    </div>
                  )}
                  {selectedFacility.village && (
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Village:
                      </span>
                      <span className="text-gray-900">
                        {selectedFacility.village}
                      </span>
                    </div>
                  )}
                  {selectedFacility.latitude && selectedFacility.longitude && (
                    <div className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Coordinates:
                      </span>
                      <span className="text-gray-900">
                        {selectedFacility.latitude.toFixed(6)},{' '}
                        {selectedFacility.longitude.toFixed(6)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setSelectedFacilityId(null)}
                className="w-full md:w-auto px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

HealthFacilityMap.propTypes = {
  facilities: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      level: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      district: PropTypes.string,
      subcounty: PropTypes.string,
    })
  ).isRequired,
  location: PropTypes.shape({
    name: PropTypes.string.isRequired,
    code: PropTypes.string,
  }),
  title: PropTypes.string.isRequired,
}

export default HealthFacilityMap
